// src/utils/gradingRules.ts

export type GradeCategory =
    | "Exploring"
    | "Helping"
    | "Reflective"
    | "Questioning"
    | "Neutral"
    | "Beginning Learner"
    | "Enthusiastic Volunteer"
    | "Committed Planner"
    | "Focused Practitioner"
    | "Experienced Seeker"
    | "Cautious"
    | "Determined"
    | "Adaptable"
    | "Faithful"
    | "Empowered"
    | "Beginning Dreamer"
    | "Collaborative Worker"
    | "Cautious Planner"
    | "Balanced Seeker"
    | "Growing Leader";

export type QuestionRule = {
    question: string;
    options: Record<string, Partial<Record<GradeCategory, number>>>;
};

export type SectionRules = Record<string, QuestionRule>;

export const gradingRules: Record<
    "Awareness" | "Knowledge" | "Mindset" | "Vision",
    SectionRules
> = {
    // 🟦 SECTION 1 — AWARENESS & INTEREST
    Awareness: {
        Q1: {
            question:
                "Let’s begin with assessing your knowledge and awareness about Missions. To which of these categories would you say you belong?",
            options: {
                "I know little to nothing about it": { Exploring: 3 },
                "I have some idea of what it’s supposed to be": { Exploring: 2, Reflective: 1 },
                "I have had the opportunity to listen first-hand to active missionaries talk about missions, and their experiences on the mission field.": {
                    Reflective: 3,
                },
                "I have participated a little (e.g. local outreach)": { Helping: 3 },
                "I am actively involved in missions": { Helping: 2, Reflective: 1 },
            },
        },
        Q2: {
            question: "What emotions or thoughts come to mind when you think about missions?",
            options: {
                "Excitement and curiosity": { Exploring: 2, Helping: 1 },
                "Overwhelmed or unsure": { Questioning: 2 },
                "Inspired and hopeful": { Reflective: 2, Helping: 1 },
                "Conflicted or hesitant": { Questioning: 3 },
                "Neutral or indifferent": { Neutral: 3 },
            },
        },
        Q3: {
            question: "How interested would you say you have been in Missions?",
            options: {
                "Very high: I have been involved, and I want to inspire others to Missions": {
                    Helping: 3,
                },
                "High: I have been very interested in learning and getting involved": {
                    Reflective: 2,
                    Helping: 1,
                },
                "Casual: I have been somewhat curious but not sure I’m ready to get deeply engaged": {
                    Exploring: 2,
                },
                "Minimal: It has hardly been in my thoughts": { Neutral: 3 },
            },
        },
        Q4: {
            question: "What is it about Missions that interests you?",
            options: {
                "Bringing the knowledge of God to people who do not have the same opportunities as I have": {
                    Helping: 2,
                },
                "Experiencing different cultures, languages, and people": { Exploring: 2 },
                "The various capacities and opportunities within mission work, e.g. translation, medical aid": {
                    Reflective: 2,
                },
                "Helping others and making a difference": { Helping: 3 },
                "Strengthening personal faith and purpose": { Reflective: 3 },
                "Unsure, just curious": { Questioning: 2 },
            },
        },
        Q5: {
            question:
                "What are your two biggest challenges in pursuing your interest in missions?",
            options: {
                "Learning about other cultures and customs": { Exploring: 2 },
                "Understanding spiritual or religious aspects": { Reflective: 1, Questioning: 1 },
                "Financial and logistical support": { Helping: 1, Questioning: 1 },
                "Commitment and lifestyle changes": { Questioning: 2 },
                "Fear of the unknown": { Questioning: 3 },
            },
        },
    },

    // 🟩 SECTION 2 — KNOWLEDGE & READINESS
    Knowledge: {
        Q6: {
            question:
                "How much do you know about the different types of missions (local, international, church-based, humanitarian)?",
            options: {
                "Very little or none": { "Beginning Learner": 3 },
                "Basic understanding": { "Beginning Learner": 2 },
                "Some general knowledge": { "Enthusiastic Volunteer": 2 },
                "I have studied or researched them": { "Committed Planner": 2 },
                "I have experience in one or more types": { "Experienced Seeker": 3 },
            },
        },
        Q7: {
            question:
                "How ready are you to commit time and effort to learning or participating in missions?",
            options: {
                "I want to explore my options": { "Beginning Learner": 2 },
                "I’d love to volunteer occasionally": { "Enthusiastic Volunteer": 2 },
                "I’m willing to participate in short-term projects": { "Committed Planner": 2 },
                "I want to engage long-term or part-time": { "Focused Practitioner": 2 },
                "I’m prepared for full-time involvement": { "Experienced Seeker": 3 },
            },
        },
        Q8: {
            question: "What kind of training or preparation interests you most for missions?",
            options: {
                "Cultural sensitivity and language skills": { "Beginning Learner": 1, "Committed Planner": 1 },
                "Practical skills (healthcare, education, construction)": { "Focused Practitioner": 2 },
                "Spiritual and personal growth": { "Enthusiastic Volunteer": 2 },
                "Logistics, planning, and fundraising": { "Committed Planner": 2 },
                "Teamwork and leadership": { "Experienced Seeker": 2 },
            },
        },
        Q9: {
            question: "How important is it for you that missions involve both spiritual and practical help?",
            options: {
                "Very important — I want both aspects": { "Focused Practitioner": 2 },
                "More practical than spiritual": { "Committed Planner": 1 },
                "More spiritual than practical": { "Enthusiastic Volunteer": 1 },
                "I’m flexible about the balance": { "Experienced Seeker": 1 },
                "I need to learn more about this": { "Beginning Learner": 2 },
            },
        },
        Q10: {
            question: "What resources or support do you think you would need to start your mission journey?",
            options: {
                "Mentorship and guidance": { "Enthusiastic Volunteer": 2 },
                "Financial and Material aid": { "Committed Planner": 2 },
                "Training and capacity building": { "Beginning Learner": 2 },
                "Community and team support": { "Focused Practitioner": 2 },
                "Information and awareness": { "Beginning Learner": 1 },
            },
        },
    },

    // 🟨 SECTION 3 — MINDSET & PERSONAL DEVELOPMENT
    Mindset: {
        Q11: {
            question: "How do you handle uncertainty and change in new environments?",
            options: {
                "I get anxious and avoid change": { Cautious: 3 },
                "I try to cope but feel stressed": { Cautious: 2 },
                "I adapt slowly but steadily": { Determined: 2 },
                "I embrace change with optimism": { Adaptable: 2 },
                "I thrive in uncertain situations": { Empowered: 3 },
            },
        },
        Q12: {
            question: "How do you approach learning new languages or cultural customs?",
            options: {
                "I find it very difficult and intimidating": { Cautious: 3 },
                "I try but get easily frustrated": { Cautious: 2 },
                "I am willing to learn with some patience": { Determined: 2 },
                "I enjoy the challenge and effort": { Adaptable: 2 },
                "I actively seek to immerse myself fully": { Empowered: 3 },
            },
        },
        Q13: {
            question: "What role do you think faith plays in missions?",
            options: {
                "I’m unsure about faith’s role": { Cautious: 2 },
                "Faith is somewhat important": { Determined: 1 },
                "Faith guides many aspects": { Faithful: 2 },
                "Faith is central and essential": { Faithful: 3 },
                "Faith motivates and sustains me fully": { Empowered: 3 },
            },
        },
        Q14: {
            question: "What kind of support system do you currently have for mission work?",
            options: {
                "I have no support yet": { Cautious: 3 },
                "A few friends or family support me": { Determined: 1 },
                "My church or community is somewhat supportive": { Faithful: 2 },
                "I belong to groups that encourage mission involvement": { Adaptable: 2 },
                "I have a strong, committed support network": { Empowered: 3 },
            },
        },
        Q15: {
            question: "What personal qualities do you believe are most important for missionaries?",
            options: {
                "Patience and humility": { Faithful: 2 },
                "Flexibility and perseverance": { Adaptable: 2 },
                "Compassion and empathy": { Faithful: 2 },
                "Strong communication and leadership": { Empowered: 2 },
                "All of the above": { Empowered: 3 },
            },
        },
    },

    // 🟥 SECTION 4 — VISION & IMPACT
    Vision: {
        Q16: {
            question: "What is your ideal vision of the impact you want to have through missions?",
            options: {
                "Providing basic needs and care": { Helping: 1, "Collaborative Worker": 1 },
                "Sharing knowledge and education": { "Balanced Seeker": 2 },
                "Guiding spiritual growth and faith": { "Beginning Dreamer": 2 },
                "Building lasting community relationships": { "Collaborative Worker": 2 },
                "Revolutionalizing abhorrent cultural practices": { "Growing Leader": 3 },
            },
        },
        Q17: {
            question: "What do you consider the biggest risk in missions?",
            options: {
                "Physical danger or health issues": { "Cautious Planner": 2 },
                "Emotional or spiritual burnout": { "Balanced Seeker": 1 },
                "Financial instability or challenges": { "Cautious Planner": 2 },
                "Cultural misunderstandings or conflict": { "Collaborative Worker": 2 },
                "Losing connection with home": { "Balanced Seeker": 1 },
            },
        },
        Q18: {
            question: "How do you envision harmonizing mission work with personal and family life?",
            options: {
                "Missions come first, personal life second": { "Growing Leader": 2 },
                "Personal life is more important": { "Cautious Planner": 1 },
                "Try to balance both": { "Balanced Seeker": 2 },
                "Seek support to maintain balance": { "Collaborative Worker": 2 },
                "Maintain healthy balance consistently": { "Balanced Seeker": 3 },
            },
        },
        Q19: {
            question:
                "If given the opportunity, would you love to learn from missionaries — hearing about their exploits and experiences across different localities and cultures?",
            options: {
                "Yes, I would be very eager to learn from their real-life stories and practical lessons": {
                    "Growing Leader": 3,
                },
                "I am interested, but I would also like some guidance on how to apply what I learn": {
                    "Collaborative Worker": 2,
                },
                "It sounds valuable, but I am not sure how relevant it would be to my current understanding": {
                    "Beginning Dreamer": 2,
                },
                "Maybe, if the stories are relatable and not too overwhelming or complex": {
                    "Balanced Seeker": 2,
                },
                "No, I prefer to learn missions through other means rather than listening to personal experiences": {
                    "Cautious Planner": 2,
                },
            },
        },
    },
};
